import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { formatPrice, formatBath, formatHoa } from "../mock/mock";
import { getProperty, mediaUrl, thumbUrl } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import {
  BedDouble, Bath, Maximize, MapPin, ArrowLeft, Check, ChevronLeft, ChevronRight,
  X, Play, Video, Building2, CalendarCheck, Tag, FileText,
} from "lucide-react";
import useReveal from "../hooks/useReveal";

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

const InfoTile = ({ label, value }) => (
  <div className="bg-slate-50 rounded-lg p-4 text-center">
    <p className="text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
    <p className="font-600 text-[#0b1f33] mt-1 text-sm">{value}</p>
  </div>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  useReveal([property]);

  useEffect(() => {
    setLoading(true);
    getProperty(id).then(setProperty).catch(() => setProperty(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="pt-40 pb-24 text-center text-slate-400">Chargement…</div>;
  if (!property) {
    return (
      <div className="pt-40 pb-24 text-center">
        <p className="text-slate-500">Propriété introuvable.</p>
        <Link to="/proprietes" className="text-[#1156a7] underline mt-4 inline-block">Retour aux propriétés</Link>
      </div>
    );
  }

  const gallery = property.gallery && property.gallery.length ? property.gallery : [property.image].filter(Boolean);
  const total = gallery.length || 1;
  const go = (d) => setIdx((i) => (i + d + total) % total);
  const isRent = property.listing_type === "location";

  return (
    <div className="pt-28 pb-24">
      <div className="max-w-6xl mx-auto px-6">
        <Link to="/proprietes" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#1156a7] mb-6">
          <ArrowLeft size={16} /> Retour aux propriétés
        </Link>

        {/* Slideshow */}
        <div className="relative h-[300px] md:h-[500px] rounded-xl overflow-hidden bg-slate-100 group">
          {gallery.length > 0 && (
            <img src={thumbUrl(gallery[idx], 960)} alt={property.title} decoding="async" className="w-full h-full object-cover cursor-zoom-in" onClick={() => setLightbox(true)} />
          )}
          <div className="absolute top-4 left-4 flex flex-col gap-2 items-start">
            <StatusBadge status={property.status} />
            {(property.tags || []).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 bg-[#1156a7] text-white text-xs font-600 px-3 py-1 rounded-full">
                <Tag size={12} /> {t}
              </span>
            ))}
          </div>
          {total > 1 && (
            <>
              <button onClick={() => go(-1)} className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white"><ChevronLeft size={22} /></button>
              <button onClick={() => go(1)} className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 flex items-center justify-center shadow hover:bg-white"><ChevronRight size={22} /></button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                {gallery.map((g, i) => (
                  <button key={g} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full ${i === idx ? "bg-white w-5" : "bg-white/50"} transition-all`} />
                ))}
              </div>
              <span className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full">{idx + 1} / {total}</span>
            </>
          )}
        </div>
        {/* thumbnails */}
        {total > 1 && (
          <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
            {gallery.map((g, i) => (
              <button key={g} onClick={() => setIdx(i)} className={`shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 ${i === idx ? "border-[#1156a7]" : "border-transparent"}`}>
                <img src={thumbUrl(g, 320)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs uppercase tracking-wider text-slate-400">{property.type === "prevente" ? "Prévente" : "Revente"}</span>
              {property.category && <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">{property.category}</span>}
            </div>
            <h1 className="font-serif-display text-4xl text-[#0b1f33]">{property.title}</h1>
            <p className="flex items-center gap-1.5 text-slate-500 mt-2">
              <MapPin size={16} className="text-[#1156a7]" /> {property.address || [property.sub_location, property.location].filter(Boolean).join(", ") || property.location}
            </p>

            {(() => {
              const bath = formatBath(property.bathrooms);
              const hasBeds = property.bedrooms != null && property.bedrooms !== 0;
              const bedsText = hasBeds ? `${property.bedrooms} ch.` : property.bedrooms_label;
              const hasArea = property.area != null && property.area !== 0;
              const areaText = hasArea ? `${property.area} m²` : property.area_label;
              const items = [];
              if (bedsText) items.push(<span key="b" className="flex items-center gap-2 text-slate-700"><BedDouble size={20} className="text-[#1156a7]" /> {bedsText}</span>);
              if (bath) items.push(<span key="s" className="flex items-center gap-2 text-slate-700"><Bath size={20} className="text-[#1156a7]" /> {bath} sdb</span>);
              if (areaText) items.push(<span key="a" className="flex items-center gap-2 text-slate-700"><Maximize size={20} className="text-[#1156a7]" /> {areaText}</span>);
              if (property.lot_area) items.push(<span key="l" className="flex items-center gap-2 text-slate-700"><Maximize size={20} className="text-[#1156a7]" /> Terrain : {new Intl.NumberFormat("fr-FR").format(property.lot_area)} m²</span>);
              return items.length ? <div className="flex flex-wrap items-center gap-8 mt-6 py-6 border-y border-slate-100">{items}</div> : null;
            })()}

            {/* Video / Virtual tour buttons */}
            {(property.video_url || property.virtual_tour_url) && (
              <div className="flex flex-wrap gap-3 mt-6">
                {property.video_url && (
                  <a href={property.video_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-md bg-[#1156a7] text-white font-600 text-sm hover:bg-[#0d447f] transition-colors">
                    <Play size={16} /> Visite commentée
                  </a>
                )}
                {property.virtual_tour_url && (
                  <a href={property.virtual_tour_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-md border border-[#1156a7] text-[#1156a7] font-600 text-sm hover:bg-[#1156a7]/5 transition-colors">
                    <Video size={16} /> Visite virtuelle
                  </a>
                )}
              </div>
            )}

            <h3 className="font-serif-display text-2xl text-[#0b1f33] mt-8 mb-3">Description</h3>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{property.description}</p>

            {property.features && property.features.length > 0 && (
              <>
                <h3 className="font-serif-display text-2xl text-[#0b1f33] mt-8 mb-4">Prestations</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {property.features.map((f) => (
                    <span key={f} className="flex items-center gap-2 text-sm text-slate-700"><Check size={16} className="text-[#1156a7]" /> {f}</span>
                  ))}
                </div>
              </>
            )}

            {((property.amenities_available || []).length > 0 || (property.amenities_unavailable || []).length > 0) && (
              <>
                <h3 className="font-serif-display text-2xl text-[#0b1f33] mt-8 mb-4">Équipements</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(property.amenities_available || []).map((a) => (
                    <span key={a} className="flex items-center gap-2 text-sm text-slate-700"><Check size={16} className="text-[#1156a7]" /> {a}</span>
                  ))}
                  {(property.amenities_unavailable || []).map((a) => (
                    <span key={a} className="flex items-center gap-2 text-sm text-slate-400 line-through"><X size={16} className="text-slate-300" /> {a}</span>
                  ))}
                </div>
              </>
            )}

            {(property.roi || property.units || property.beach_distance || property.delivery_label) && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
                {property.delivery_label && <InfoTile label="Livraison" value={property.delivery_label} />}
                {property.roi && <InfoTile label="Rendement (ROI)" value={property.roi} />}
                {property.units && <InfoTile label="Unités" value={property.units} />}
                {property.beach_distance && <InfoTile label="Distance plage" value={property.beach_distance} />}
              </div>
            )}

            {(property.videos || []).length > 0 && (
              <>
                <h3 className="font-serif-display text-2xl text-[#0b1f33] mt-8 mb-4">Vidéos</h3>
                <div className="space-y-4">
                  {property.videos.map((v, i) => {
                    const emb = ytEmbed(v);
                    return emb ? (
                      <div key={v} className="relative w-full rounded-xl overflow-hidden" style={{ paddingTop: "56.25%" }}>
                        <iframe className="absolute inset-0 w-full h-full" src={emb} title={`video-${i}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      </div>
                    ) : (
                      <a key={v} href={v} target="_blank" rel="noreferrer" className="text-[#1156a7] underline">{v}</a>
                    );
                  })}
                </div>
              </>
            )}

            {property.pdf_url && (
              <a href={property.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-8 px-5 py-3 rounded-md border border-[#1156a7] text-[#1156a7] font-600 text-sm hover:bg-[#1156a7]/5 transition-colors">
                <FileText size={16} /> Fiche PDF
              </a>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-[#0b1f33] text-white rounded-xl p-8">
              <p className="text-white/60 text-sm">Prix {isRent ? "de location" : "de vente"}</p>
              <p className="font-serif-display text-4xl text-[#5b96dd] mt-1">
                {formatPrice(property.price, property.currency, property.price_from)}{isRent && property.price != null && <span className="text-base text-white/50"> /mois</span>}
              </p>

              <div className="mt-6 space-y-3 text-sm border-t border-white/10 pt-6">
                {formatHoa(property.hoa) && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-white/70"><Building2 size={16} className="text-[#5b96dd]" /> Frais de condo</span>
                    <span className="font-600">{formatHoa(property.hoa)} / mois</span>
                  </div>
                )}
                {property.delivery_label ? (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-white/70"><CalendarCheck size={16} className="text-[#5b96dd]" /> Livraison</span>
                    <span className="font-600">{property.delivery_label}</span>
                  </div>
                ) : property.delivery_date ? (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-white/70"><CalendarCheck size={16} className="text-[#5b96dd]" /> Livraison</span>
                    <span className="font-600">{new Date(property.delivery_date + "T00:00:00").toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</span>
                  </div>
                ) : null}
              </div>

              <Link to="/contact" className="btn-gold block text-center mt-6 py-3.5 rounded-sm font-600">Demander une visite</Link>
              <Link to="/contact" className="block text-center mt-3 py-3.5 rounded-sm font-600 border border-white/30 hover:bg-white/10 transition-colors">Options de financement</Link>
            </div>
          </aside>
        </div>
      </div>

      {/* Fullscreen lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center" onClick={() => setLightbox(false)}>
          <button className="absolute top-5 right-5 text-white/80 hover:text-white z-10"><X size={30} /></button>
          <img src={mediaUrl(gallery[idx])} alt="" className="max-h-[90vh] max-w-[92vw] object-contain" onClick={(e) => e.stopPropagation()} />
          {total > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25"><ChevronLeft size={26} /></button>
              <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25"><ChevronRight size={26} /></button>
              <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">{idx + 1} / {total}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
